import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    Link,
    Radio,
    RadioGroup,
    Switch,
    TextField,
    Typography,
} from '@mui/material';

import { handoffRedirectTarget } from '../../auth/oauthHandoff';
import config from '../../config';
import { ownBeginHop } from '../api';
import { useSignup } from '../context';
import { connectReturnText, ERROR_WORDS, errorText } from '../labels';
import { openAuthorizePage } from '../navigate';
import { pickConnectReturn } from '../storage';
import type { ConnectReturn } from '../storage';
import type { Connection, CredentialMode, Credentials, Draft, Restech } from '../types';

// Connect (plan §16, §12; easy-onboarding-bokun §6.3, §6.5): the booking systems this invite may use and how each
// connects, all from `GET restechs` — nothing about a booking system is hard-coded here.
//
// - A booking system with one way to connect (FareHarbor) answers only `credential_fields`: one text form.
// - One with `credential_modes` shows its primary mode; `advanced` modes sit behind the "Advanced: use API keys"
//   toggle; a mode that is not `available` here is not offered.
// - A `text` mode's fields are pasted as `credentials` and go to `accounts` / `account` (and later `create`) only;
//   they stay in memory.
// - An `oauth` mode's fields (e.g. the domain) go to `oauth/start`; the page then leaves for `authorize_url` and the
//   booking system sends the browser back with `?<restech>=connected|error&reason=` (storage.ts). Once `restechs`
//   answers a `connection`, the page continues with empty credentials: the server uses the account it holds.

const ALLOW_HTTP = config.env === 'dev';

/** The ways to connect `restech`, primary first: its `credential_modes`, or one mode made of its
 *  `credential_fields` (a booking system with a single way to connect). */
export function modesOf(restech: Restech): CredentialMode[] {
    if (restech.credential_modes && restech.credential_modes.length) {
        return restech.credential_modes;
    }
    const oauth = restech.credential_fields.some((f) => f.kind === 'oauth');
    return [{ code: 'default', label: '', fields: restech.credential_fields, kind: oauth ? 'oauth' : 'text', advanced: false, available: true }];
}

/** `value` cut back to the part before a field's read-only `suffix` (e.g. `.bokun.io`) when it ends with it, or with it
 *  without its leading dot: the whole address typed or pasted where only its first part is asked. Case-insensitive,
 *  trailing spaces ignored; any other value is returned as it is. */
export function withoutSuffix(value: string, suffix?: string): string {
    if (!suffix) {
        return value;
    }
    const trimmed = value.trimEnd();
    const end = [suffix, suffix.replace(/^\./, '')].find((s) => s && trimmed.toLowerCase().endsWith(s.toLowerCase()));
    return end ? trimmed.slice(0, trimmed.length - end.length) : value;
}

/** The booking system to preselect: the one the page came back from, the one chosen before, a connected one,
 *  the one this browser chose before the reload, or the only one. */
function preselect(list: Restech[], current: string | null, back: ConnectReturn | null, resume: Draft | null): string | null {
    const known = (code: string | null | undefined) => (code && list.some((r) => r.code === code) ? code : null);
    return known(back?.restech)
        || known(current)
        || (list.find((r) => !!r.connection) || null)?.code
        || known(resume?.restech)
        || (list.length === 1 ? list[0].code : null);
}

function fieldErrorsOf(error: any, names: string[]): Record<string, string> {
    const errors = error && error.errors && typeof error.errors === 'object' ? error.errors : {};
    // Pasted credentials answer under `credentials`; the `oauth/start` body's fields at the top level.
    const nested = errors.credentials && typeof errors.credentials === 'object' ? errors.credentials : {};
    const out: Record<string, string> = {};
    const add = (name: string, messages: unknown) => {
        out[name] = Array.isArray(messages) ? messages.join(' ') : String(messages);
    };
    Object.entries(nested).forEach(([name, messages]) => add(name, messages));
    names.filter((name) => name in errors && !(name in out)).forEach((name) => add(name, errors[name]));
    return out;
}

export default function Connect() {
    const { api, flow, dispatch, sessionLost } = useSignup();
    const [restechs, setRestechs] = React.useState<Restech[] | null>(null);
    const [loadError, setLoadError] = React.useState<unknown>(null);
    const [loadSeq, setLoadSeq] = React.useState(0);
    const [selected, setSelected] = React.useState<string | null>(flow.restech ? flow.restech.code : null);
    const [values, setValues] = React.useState<Credentials>(flow.credentials || {});
    const [missing, setMissing] = React.useState<string[]>([]);
    const [busy, setBusy] = React.useState(false);
    const [leaving, setLeaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(flow.flash ? flow.flash.message : null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [advancedOpen, setAdvancedOpen] = React.useState(false);
    const [modeCode, setModeCode] = React.useState<string | null>(null);
    // Booking systems whose connection the client set aside ("Connect a different account") or the server no
    // longer holds (409 not_connected): their connect form shows again.
    const [setAside, setSetAside] = React.useState<string[]>([]);
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);
    // Back from the booking system's page out of the back/forward cache: the page is as it was left ("Opening …").
    React.useEffect(() => {
        const onPageShow = (ev: PageTransitionEvent) => {
            if (ev.persisted) {
                setLeaving(false);
                setBusy(false);
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    const continueWith = async (r: Restech, connection: Connection) => {
        setBusy(true);
        setError(null);
        setFieldErrors({});
        try {
            const view = await api.account(r.code, {}, connection.account);
            const accounts = [{ id: connection.account, name: connection.account_name }];
            dispatch({ type: 'account_viewed', restech: r, credentials: {}, accounts, account: connection.account, view });
            return;
        } catch (e: any) {
            if (sessionLost(e)) { return; }
            if (mounted.current) {
                if (e && e.code === 'not_connected') {
                    setSetAside((codes) => [...codes, r.code]);
                }
                setError(errorText(e));
            }
        }
        if (mounted.current) { setBusy(false); }
    };

    React.useEffect(() => {
        let active = true;
        setLoadError(null);
        api.restechs().then(
            ({ restechs: list, sandbox_offered: sandboxOffered }) => {
                if (!active) { return; }
                dispatch({ type: 'restechs_listed', sandboxOffered: sandboxOffered === true });
                // Acted on only for a restech code this list has.
                const back = pickConnectReturn(flow.connectReturns, list.map((r) => r.code));
                const code = preselect(list, selected, back, flow.resume);
                const chosen = list.find((r) => r.code === code) || null;
                setRestechs(list);
                setSelected(code);
                if (flow.connectReturns.length) {
                    dispatch({ type: 'connect_return_seen' });
                }
                if (back) {
                    const returned = list.find((r) => r.code === back.restech);
                    if (returned && back.outcome === 'error') {
                        setError(connectReturnText(back.reason, returned.display_name));
                    } else if (returned && !returned.connection) {
                        setError(connectReturnText('not_connected', returned.display_name));
                    }
                }
                // Arriving here after the claim (or back from the booking system) with an account connected: go on
                // with it. Coming back to this screen ("Back") shows it instead.
                if (flow.autoConnect && chosen && chosen.connection && !(back && back.outcome === 'error')) {
                    continueWith(chosen, chosen.connection);
                }
            },
            (e) => {
                if (!active || sessionLost(e)) { return; }
                setLoadError(e);
            },
        );
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per attempt
    }, [api, loadSeq]);

    const restech = restechs && selected ? restechs.find((r) => r.code === selected) || null : null;
    const connection = restech && restech.connection && !setAside.includes(restech.code) ? restech.connection : null;
    const allModes = restech ? modesOf(restech) : [];
    const usable = allModes.filter((m) => m.available !== false);
    const primaryModes = usable.filter((m) => !m.advanced);
    const advancedModes = usable.filter((m) => m.advanced);
    const unavailable = allModes.filter((m) => m.available === false && !m.advanced);
    const showAdvanced = advancedModes.length > 0 && (advancedOpen || primaryModes.length === 0);
    const shownModes = showAdvanced ? advancedModes : primaryModes;
    const mode = shownModes.find((m) => m.code === modeCode) || shownModes[0] || null;
    const oauthMode = allModes.find((m) => m.kind === 'oauth') || null;
    const helpUrl = restech ? handoffRedirectTarget(restech.help_url, { allowHttp: ALLOW_HTTP }) : null;
    const connectedLabel = connection && oauthMode
        ? oauthMode.fields.map((f) => connection[f.name]).filter((v) => v && v !== connection.account_name).join(' · ')
        : '';

    const resetForm = () => {
        setMissing([]);
        setFieldErrors({});
        setError(null);
    };

    const choose = (code: string) => {
        if (code === selected) { return; }
        setSelected(code);
        setValues({});
        setAdvancedOpen(false);
        setModeCode(null);
        resetForm();
    };

    // Only our own begin hop is followed: the API origin this client calls (https; http in local development).
    const startOAuth = async (r: Restech, fields: Record<string, string>) => {
        let target: string | null = null;
        try {
            const started = await api.oauthStart(r.code, fields);
            target = ownBeginHop(started.authorize_url, { allowHttp: ALLOW_HTTP });
            if (!target && mounted.current) {
                setError(ERROR_WORDS.server_error);
            }
        } catch (e) {
            if (sessionLost(e)) { return; }
            if (mounted.current) {
                setError(errorText(e));
                setFieldErrors(fieldErrorsOf(e, Object.keys(fields)));
            }
        }
        if (target) {
            if (mounted.current) { setLeaving(true); }
            openAuthorizePage(target);
            return;
        }
        if (mounted.current) { setBusy(false); }
    };

    const onSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (!restech || busy) { return; }
        if (connection) {
            continueWith(restech, connection);
            return;
        }
        if (!mode) { return; }
        const entered: Credentials = {};
        mode.fields.forEach((f) => {
            entered[f.name] = (values[f.name] || '').trim();
        });
        const empty = mode.fields.filter((f) => !entered[f.name]).map((f) => f.name);
        setMissing(empty);
        setFieldErrors({});
        setError(null);
        if (empty.length) { return; }
        setBusy(true);
        if (mode.kind === 'oauth') {
            await startOAuth(restech, entered);
            return;
        }
        try {
            const { accounts } = await api.accounts(restech.code, entered);
            if (accounts.length === 0) {
                setError(ERROR_WORDS.no_companies);
            } else if (accounts.length === 1) {
                const view = await api.account(restech.code, entered, accounts[0].id);
                dispatch({ type: 'account_viewed', restech, credentials: entered, accounts, account: accounts[0].id, view });
                return;
            } else {
                dispatch({ type: 'accounts_listed', restech, credentials: entered, accounts });
                return;
            }
        } catch (e) {
            if (sessionLost(e)) { return; }
            if (mounted.current) {
                setError(errorText(e));
                setFieldErrors(fieldErrorsOf(e, mode.fields.map((f) => f.name)));
            }
        }
        if (mounted.current) { setBusy(false); }
    };

    if (loadError) {
        return (
            <Box>
                <Typography variant="h5" component="h1" gutterBottom>Connect your booking system</Typography>
                <Alert severity="error" sx={{ mb: 2 }}>{errorText(loadError)}</Alert>
                <Button variant="outlined" onClick={() => setLoadSeq((n) => n + 1)}>Try again</Button>
            </Box>
        );
    }
    if (!restechs) {
        return (
            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                <CircularProgress size={24} />
                <Typography>Loading…</Typography>
            </Box>
        );
    }

    let submitLabel = 'Connect';
    if (connection) {
        submitLabel = busy ? 'Connecting…' : 'Continue';
    } else if (mode && mode.kind === 'oauth') {
        submitLabel = busy ? `Opening ${restech!.display_name}…` : (mode.label || `Connect with ${restech!.display_name}`);
    } else if (busy) {
        submitLabel = 'Connecting…';
    }

    return (
        <Box component="form" noValidate onSubmit={onSubmit} aria-label="Connect your booking system">
            <Typography variant="h5" component="h1" gutterBottom>Connect your booking system</Typography>
            <Typography color="text.secondary" paragraph>
                TourOptima reads your company, tours and bookings from your booking system. Choose it and enter the access
                details it gave you.
            </Typography>
            {flow.resume && !flow.restech && !connection && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    For your security we do not keep your booking-system details. Enter them again to continue
                    with {flow.resume.account_name}.
                </Alert>
            )}
            {restechs.length === 0 ? (
                <Alert severity="warning">
                    Signing up cannot continue right now: no booking system is available for your invitation. Please contact
                    your Curated Planet contact.
                </Alert>
            ) : (
                <>
                    <Box role="group" aria-label="Booking system"
                        sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5, mb: 3 }}>
                        {restechs.map((r) => {
                            const active = r.code === selected;
                            return (
                                <ButtonBase
                                    key={r.code}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => choose(r.code)}
                                    disabled={busy || leaving}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        p: 2,
                                        borderRadius: 1,
                                        border: 2,
                                        borderColor: active ? 'primary.main' : 'divider',
                                        bgcolor: active ? 'action.selected' : 'background.paper',
                                        textAlign: 'left',
                                        typography: 'subtitle1',
                                        fontWeight: active ? 600 : 400,
                                    }}
                                >
                                    {r.display_name}
                                </ButtonBase>
                            );
                        })}
                    </Box>
                    {restech && connection && (
                        <Box data-testid="signup-connection">
                            <Alert severity="success">
                                Connected to {restech.display_name}: <strong>{connection.account_name}</strong>
                                {connectedLabel ? ` (${connectedLabel})` : ''}
                            </Alert>
                            <Button type="button" size="small" sx={{ mt: 1 }} disabled={busy}
                                onClick={() => { setSetAside((codes) => [...codes, restech.code]); resetForm(); }}>
                                Connect a different account
                            </Button>
                        </Box>
                    )}
                    {restech && !connection && (
                        <Box>
                            {unavailable.length > 0 && usable.length > 0 && (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    {unavailable.map((m) => m.label || restech.display_name).join(', ')} is not available right now.
                                </Alert>
                            )}
                            {usable.length === 0 && (
                                <Alert severity="warning">
                                    Connecting {restech.display_name} is not available right now. Please try again later.
                                </Alert>
                            )}
                            {shownModes.length > 1 && (
                                <RadioGroup aria-label="How to connect" value={mode ? mode.code : ''}
                                    onChange={(e) => { setModeCode(e.target.value); resetForm(); }}>
                                    {shownModes.map((m) => (
                                        <FormControlLabel key={m.code} value={m.code} control={<Radio />} label={m.label || m.code}
                                            disabled={busy || leaving} />
                                    ))}
                                </RadioGroup>
                            )}
                            {mode && mode.fields.map((f, i) => (
                                <TextField
                                    key={`${mode.code}-${f.name}`}
                                    id={`signup-credential-${f.name}`}
                                    name={f.name}
                                    label={f.label}
                                    type={f.secret ? 'password' : 'text'}
                                    value={values[f.name] || ''}
                                    onChange={(e) => {
                                        // Read now: the updater may run after React restored the controlled value.
                                        const value = withoutSuffix(e.target.value, f.suffix);
                                        setValues((v) => ({ ...v, [f.name]: value }));
                                    }}
                                    fullWidth
                                    margin="normal"
                                    required
                                    autoFocus={i === 0}
                                    autoComplete="off"
                                    disabled={busy || leaving}
                                    inputProps={{ spellCheck: false, autoCapitalize: 'off', maxLength: 256 }}
                                    InputProps={f.suffix ? {
                                        endAdornment: <InputAdornment position="end">{f.suffix}</InputAdornment>,
                                    } : undefined}
                                    error={missing.includes(f.name) || !!fieldErrors[f.name]}
                                    helperText={fieldErrors[f.name] || (missing.includes(f.name) ? 'Required.' : f.hint) || ' '}
                                />
                            ))}
                            {mode && mode.kind === 'text' && mode.advanced && (
                                <Alert severity="info" sx={{ mt: 1 }} data-testid="signup-manual-webhook-note">
                                    With API keys you set up booking notifications in {restech.display_name} yourself. Your
                                    onboarding page shows what to add once your account is ready.
                                </Alert>
                            )}
                            {mode && mode.kind === 'text' && helpUrl && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <Link href={helpUrl} target="_blank" rel="noopener noreferrer">
                                        Where do I find these details?
                                    </Link>
                                </Typography>
                            )}
                            {advancedModes.length > 0 && primaryModes.length > 0 && (
                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={(
                                        <Switch
                                            checked={advancedOpen}
                                            onChange={(e) => { setAdvancedOpen(e.target.checked); setModeCode(null); resetForm(); }}
                                            disabled={busy || leaving}
                                        />
                                    )}
                                    label="Advanced: use API keys"
                                />
                            )}
                        </Box>
                    )}
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    <Box sx={{ mt: 3 }}>
                        <Button type="submit" variant="contained" disabled={!restech || (!connection && !mode) || busy || leaving}
                            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
                            {submitLabel}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}
